import type { stacks } from "@dropbox/api-v2-client";
import { Avatar } from "@dropbox/dig-components/avatar";
import { StylelessButton } from "@dropbox/dig-components/buttons";
import { Box } from "@dropbox/dig-foundations";
import { DashFacepile } from "@mirage/mosaics/DCLWrappers/DashFacepile";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { getInitials } from "@mirage/shared/account";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { membersFromStack } from "@mirage/stacks/utils/helpers";
import i18n from "@mirage/translations";
import styles from "./StackMembersFacepile.module.css";

type StackMembersFacepileProps = {
  onClick: () => void;
  stack: stacks.Stack;
};

export function StackMembersFacepile({
  onClick,
  stack,
}: StackMembersFacepileProps) {
  const currentAccount = useDropboxAccount();

  const isWelcomeStack =
    stack?.stack_data?.creation_type?.[".tag"] === "welcome_stack";
  const members = membersFromStack(stack, true);
  const stackOwners = (stack?.sharing_data?.members ?? []).filter(
    (member) => member.permission?.[".tag"] === "owner",
  );
  const owner = stackOwners[0];
  const ownerDisplayName =
    (owner?.subject?.[".tag"] === "user"
      ? owner.subject.display_name
      : currentAccount?.name.display_name) ?? "";
  const ownerInitials = getInitials(ownerDisplayName);
  const createdBy = isWelcomeStack
    ? i18n.t("created_by_dropbox_dash")
    : i18n.t("created_by", { name: ownerDisplayName });

  if (!currentAccount) {
    return null;
  }

  return (
    <Box display="flex" marginRight="4">
      <DigTooltip title={createdBy}>
        <StylelessButton className={styles.facepileItem} onClick={onClick}>
          <Avatar
            size="small"
            src={
              owner?.subject?.[".tag"] === "user"
                ? owner.subject.profile_photo_url
                : currentAccount.profile_photo_url
            }
          >
            {isWelcomeStack ? "DD" : ownerInitials}
          </Avatar>
        </StylelessButton>
      </DigTooltip>
      {members.length > 0 && (
        <div style={{ paddingLeft: 4 }}>
          <DashFacepile
            onAvatarClick={onClick}
            members={members}
            size="small"
          />
        </div>
      )}
    </Box>
  );
}
